import React, { createContext, useState, useContext, ReactNode } from 'react';
import { en } from './translations/en';
import { vi } from './translations/vi';

export type Language = 'en' | 'vi';

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation dictionary
const translations = {
    en,
    vi,
};

interface LanguageProviderProps {
    children: ReactNode;
}

const LANGUAGE_STORAGE_KEY = 'tps-logistics-language';

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    // Load initial language from localStorage or default to 'en'
    const [language, setLanguageState] = useState<Language>(() => {
        const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        return (storedLanguage === 'en' || storedLanguage === 'vi') ? storedLanguage : 'en';
    });

    // Wrapper function to persist language changes
    const setLanguage = (lang: Language) => {
        setLanguageState(lang);
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    };

    const t = (key: string): string => {
        const translation = translations[language] as Record<string, string>;
        return translation[key] || key;
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = (): LanguageContextType => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
